/**
 * CoursesSection — Course list, add course, remove course
 */

import React, { useState } from 'react';
import {
  VStack,
  HStack,
  Text,
  Pressable,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import Ionicons from '../ui/Ionicons';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { SectionCard } from '../ui';
import { useUserPreferencesStore } from '../../store/userPreferencesStore';
import { selectCoursePreferences, selectActiveCourseId, selectMaxCourses, selectRemoveCourse } from '../../store/userPreferencesSelectors';
import { getLanguageDisplayName } from '../../i18n';
import { getBaseLanguage } from '../../types/locale';
import { ConfirmDialog } from '../ui/ConfirmDialog';

const REMOVE_BUTTON_SIZE = 44;

export const CoursesSection = React.memo(function CoursesSection() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t, i18n } = useTranslation();
  const navigation = useNavigation('Profile');

  const coursePreferences = useUserPreferencesStore(selectCoursePreferences);
  const activeCourseId = useUserPreferencesStore(selectActiveCourseId);
  const maxCourses = useUserPreferencesStore(selectMaxCourses);
  const removeCourse = useUserPreferencesStore(selectRemoveCourse);

  const [courseToRemove, setCourseToRemove] = useState<string | null>(null);

  return (
    <>
      <SectionCard title={t('profile.courses.title')}>
        <VStack space="sm">
          <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
            {t('profile.courses.list')}
          </Text>

          {/* Course cards — tap navigates to settings */}
          {coursePreferences.map((pref) => {
            const sourceName = getLanguageDisplayName(getBaseLanguage(pref.sourceLocale), i18n.language);
            const targetName = getLanguageDisplayName(getBaseLanguage(pref.targetLocale), i18n.language);
            const isActive = pref.courseId === activeCourseId;
            const canRemove = coursePreferences.length > 1;
            return (
              <Pressable
                key={pref.courseId}
                testID={`profile-course-${pref.courseId}`}
                accessibilityLabel={`${sourceName} → ${targetName}, ${t('profile.courses.languageSettings')}`}
                accessibilityRole="button"
                onPress={() => navigation.navigate('LanguageSettings')}
              >
                <HStack
                  style={{ backgroundColor: theme.inputBg, borderWidth: isActive ? 1 : 0, borderColor: colors.primary }}
                  p="$4"
                  rounded="$lg"
                  space="sm"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <HStack space="sm" alignItems="center" style={{ flex: 1 }}>
                    <Text allowFontScaling={false} style={{ fontSize: 20 }}>🌐</Text>
                    <VStack>
                      <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
                        {sourceName} → {targetName}
                      </Text>
                      {isActive && (
                        <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: colors.primary, ...ffs }}>
                          {t('profile.courses.activeCourse')}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                  <HStack space="md" alignItems="center">
                    {canRemove && (
                      <Pressable
                        testID={`profile-course-remove-${pref.courseId}`}
                        accessibilityLabel={t('common.remove')}
                        accessibilityRole="button"
                        onPress={(e) => {
                          e.stopPropagation?.();
                          setCourseToRemove(pref.courseId);
                        }}
                        style={{
                          width: REMOVE_BUTTON_SIZE,
                          height: REMOVE_BUTTON_SIZE,
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Ionicons name="trash-outline" size={20 * fontScale} color={theme.error} />
                      </Pressable>
                    )}
                    <Ionicons name="chevron-forward" size={18 * fontScale} color={theme.textSecondary} />
                  </HStack>
                </HStack>
              </Pressable>
            );
          })}

          {/* Add course button */}
          {coursePreferences.length < maxCourses && (
            <Pressable
              testID="profile-button-add-course"
              accessibilityLabel={t('profile.courses.addCourse')}
              accessibilityRole="button"
              onPress={() => navigation.navigate('LanguageSettings')}
            >
              <HStack
                style={{ backgroundColor: theme.inputBg, borderWidth: 1, borderColor: theme.border, borderStyle: 'dashed' }}
                p="$4"
                rounded="$lg"
                space="sm"
                justifyContent="center"
                alignItems="center"
              >
                <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: colors.primary, fontWeight: '600', ...ffs }}>
                  + {t('profile.courses.addCourse')}
                </Text>
              </HStack>
            </Pressable>
          )}

          {/* Over limit warning */}
          {coursePreferences.length > maxCourses && (
            <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.error, ...ffs }}>
              {t('profile.courses.courseLimitExceeded', { max: maxCourses })}
            </Text>
          )}
        </VStack>
      </SectionCard>

      {/* Remove course confirmation */}
      <ConfirmDialog
        visible={!!courseToRemove}
        title={t('profile.courses.removeCourseTitle')}
        message={t('profile.courses.removeCourseMessage')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          if (courseToRemove) {
            removeCourse(courseToRemove);
          }
          setCourseToRemove(null);
        }}
        onCancel={() => setCourseToRemove(null)}
      />
    </>
  );
});
